import { PopUpMessageContext, useApp, useAuth } from '@hb/react'
import { defaultStageValidate, FieldMap, InsuranceInfo, UploadsProgress } from '@hb/shared'
import { FORM_ERROR, FormApi } from 'final-form'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { auth } from '../../../../backend/auth'
import { processFieldMapData } from '../../../../backend/db/utils/processData'
import { useFirebaseApp } from '../../../../contexts/FirebaseAppProvider'
import { InnerForm } from '../../FinalForm/InnerForm'
import { OnFormSubmit } from '../../FinalForm/types'
import { RenderStageProps } from '../types'
import { getInsuranceInfoValidationField } from './field'
export interface InsuranceInfoStageContextType {
  isSaving: boolean
  onSaveChanges: (updates?: Partial<InsuranceInfo>) => Promise<void>
  uploads: UploadsProgress | null
  insuranceInfoData: Partial<InsuranceInfo>
  registerForm?: (form: FormApi | null) => void
  type: 'pregnancy' | 'gyn'
  storagePath: string | null
  field: FieldMap
  setUploads: (uploads: UploadsProgress | null) => void
  update: OnFormSubmit<InsuranceInfo>
  isRefetching: boolean
  // forceSave: OnFormSubmit<InsuranceInfo>
  // primaryCoverageStoragePath: string | null
  // secondaryCoverageStoragePath: string | null
}
export const InsuranceInfoStageContext = createContext<InsuranceInfoStageContextType>({
  isSaving: false,
  onSaveChanges: () => Promise.resolve(),
  uploads: {},
  insuranceInfoData: {},
  type: 'pregnancy',

  storagePath: null,
  field: { name: '', children: {} },
  setUploads: () => {},
  update: () => Promise.resolve(undefined),
  // forceSave: () => Promise.resolve(undefined),
  isRefetching: false,
})

export const InsuranceInfoStageProvider = ({
  children,
  // assessmentId,
  // patientId,
  data,
  onSubmit,
  readOnly,
  storagePath,
  type,
  registerForm,
}: PropsWithChildren<RenderStageProps & { type: 'pregnancy' | 'gyn' }>) => {
  // const [insuranceInfoData, setInsuranceInfoData] = useState<Partial<InsuranceInfo>>({
  //   ...data,
  // })

  const { admin } = useAuth()
  const { auth: customAuth, storage: customStorage } = useFirebaseApp()
  const { appName } = useApp()

  const fetchedAt = useRef<number | null>(null)
  const updatedAt = useRef<number | null>(null)
  const [isRefetching, setIsRefetching] = useState(false)
  const updateIsRefetching = useCallback(() => {
    if (!fetchedAt.current || !updatedAt.current) {
      setIsRefetching(false)
      return
    }
    if (fetchedAt.current < updatedAt.current) {
      setIsRefetching(true)
      return
    }
    setIsRefetching(false)
  }, [])

  const { showMessage } = useContext(PopUpMessageContext)
  const [uploads, setUploads] = useState<UploadsProgress | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (data) {
      fetchedAt.current = Date.now()
      updateIsRefetching()
    }
  }, [data, updateIsRefetching])

  const getOnSubmit = useCallback<(forceSave?: boolean) => OnFormSubmit<InsuranceInfo>>(
    // async (submitted: FieldMapValue, merge = true, forceSave = false) => {
    (forceSave = false) =>
      async (updatedData, onUploadProgress, form, closeOnSuccess = true) => {
        if (readOnly) return { [FORM_ERROR]: 'Unable to update' }
        if (!storagePath) {
          showMessage({
            text: 'Something went wrong',
            subText: 'Unable to get storage path for files',
            type: 'error',
          })
          return { [FORM_ERROR]: 'Unable to get storage path for files' }
        }
        const uid = customAuth?.currentUser?.uid ?? auth.currentUser?.uid
        if (!uid) {
          showMessage({
            text: 'Not logged in',
            type: 'error',
          })
          return { [FORM_ERROR]: 'Not logged in' }
        }
        // const updated = merge ? { ...insuranceInfoData, ...submitted } : submitted
        const newValidationField = getInsuranceInfoValidationField(updatedData, false)
        const errors = defaultStageValidate(newValidationField)(updatedData)
        const hasErrors = Object.keys(errors ?? {}).length > 0
        setIsSaving(true)
        // setInsuranceInfoData(updated)
        // try {
        updatedAt.current = Date.now()
        updateIsRefetching()
        if (!hasErrors || forceSave) {
          const processed = await processFieldMapData(
            appName,
            admin,
            uid,
            storagePath,
            newValidationField,
            updatedData,
            data,
            setUploads,
            undefined,
            undefined,
            true,
            customStorage,
          )
          const res = await onSubmit(
            processed,
            progress => {
              setUploads(progress)
              onUploadProgress(progress)
            },
            form,
            !hasErrors && closeOnSuccess,
          ) //, false)
          // if (!hasErrors && !res && onClose) onClose()

          setIsSaving(false)
          return res
        } else {
          setIsSaving(false)
          return { [FORM_ERROR]: 'Validation errors' }
        }
      },
    [
      onSubmit,
      showMessage,
      data,
      readOnly,
      storagePath,
      customAuth,
      appName,
      admin,
      updateIsRefetching,
      customStorage,
    ],
  )

  const update = useMemo(() => getOnSubmit(true), [getOnSubmit])
  // const primaryCoverageStoragePath = useMemo(
  //   () =>
  //     assessmentId
  //       ? `${ASSESSMENTS}/${assessmentId}/data/insurancePlans/primary`
  //       : `${USERS}/${patientId}/insurancePlans/primary`,
  //   [assessmentId, patientId],
  // )

  // const secondaryCoverageStoragePath = useMemo(
  //   () =>
  //     assessmentId
  //       ? `${ASSESSMENTS}/${assessmentId}/data/insurancePlans/secondary`
  //       : `${USERS}/${patientId}/insurancePlans/secondary`,
  //   [assessmentId, patientId],
  // )

  const onSaveChanges = useCallback(
    async (updates?: Partial<InsuranceInfo>) => {
      await onSubmit({ ...data, ...updates }, setUploads, undefined, false) //, false)
    },
    [onSubmit, data],
  )

  const field = useMemo(() => getInsuranceInfoValidationField(data, admin), [data, admin])

  const contextData = useMemo<InsuranceInfoStageContextType>(
    () => ({
      isSaving: isSaving || isRefetching,
      update,
      // forceSave,
      uploads,
      insuranceInfoData: data,
      onSaveChanges,
      registerForm,
      type,
      storagePath,
      field,
      setUploads,
      isRefetching,
    }),
    [
      isSaving,
      update,
      // forceSave,
      uploads,
      data,
      onSaveChanges,
      registerForm,
      field,
      type,
      // primaryCoverageStoragePath,
      // secondaryCoverageStoragePath,
      storagePath,
      isRefetching,
    ],
  )
  return (
    <InnerForm
      data={data}
      key="insurance-info-stage"
      onSubmit={update}
      field={field}
      setUploads={setUploads}
      registerForm={registerForm}>
      <InsuranceInfoStageContext.Provider value={contextData}>
        {children}
      </InsuranceInfoStageContext.Provider>
    </InnerForm>
  )
}

export const useInsuranceInfoStage = () => useContext(InsuranceInfoStageContext)
